// InvoiceForge engine — pure logic, no API.

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
}

export interface Invoice {
  id: string;
  number: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  date: string;          // ISO yyyy-mm-dd
  dueDate: string;
  from: { name: string; email: string; address: string; taxId?: string };
  to: { name: string; email: string; address: string };
  items: LineItem[];
  taxRate: number;       // percentage
  notes: string;
  currency: string;      // USD, CAD, EUR, GBP, INR
  paymentLink?: string;  // Stripe/PayPal link
  paidDate?: string;
}

export function emptyInvoice(): Invoice {
  const today = new Date().toISOString().slice(0, 10);
  const due = new Date(Date.now() + 14 * 86400_000).toISOString().slice(0, 10);
  const seq = (Date.now() % 100000).toString().padStart(5, '0');
  return {
    id: crypto.randomUUID(),
    number: `INV-${seq}`,
    status: 'draft',
    date: today,
    dueDate: due,
    from: { name: '', email: '', address: '' },
    to: { name: '', email: '', address: '' },
    items: [{ id: crypto.randomUUID(), description: '', quantity: 1, rate: 0 }],
    taxRate: 0,
    notes: 'Thank you for your business. Payment due within 14 days.',
    currency: 'USD',
  };
}

export function subtotal(items: LineItem[]): number {
  return items.reduce((s, i) => s + (i.quantity || 0) * (i.rate || 0), 0);
}

export function taxAmount(items: LineItem[], taxRate: number): number {
  return subtotal(items) * (taxRate / 100);
}

export function total(items: LineItem[], taxRate: number): number {
  return subtotal(items) + taxAmount(items, taxRate);
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function isOverdue(inv: Invoice): boolean {
  if (inv.status === 'paid') return false;
  return new Date(inv.dueDate) < new Date();
}

export function exportInvoiceMarkdown(inv: Invoice): string {
  const sub = subtotal(inv.items);
  const tax = taxAmount(inv.items, inv.taxRate);
  const tot = total(inv.items, inv.taxRate);
  return `# Invoice ${inv.number}

**Date:** ${inv.date}
**Due:** ${inv.dueDate}
**Status:** ${inv.status.toUpperCase()}

---

**From**
${inv.from.name}
${inv.from.address}
${inv.from.email}${inv.from.taxId ? `\nTax ID: ${inv.from.taxId}` : ''}

**To**
${inv.to.name}
${inv.to.address}
${inv.to.email}

---

| # | Description | Qty | Rate | Amount |
|---|---|---|---|---|
${inv.items.map((i, ix) => `| ${ix + 1} | ${i.description} | ${i.quantity} | ${formatMoney(i.rate, inv.currency)} | ${formatMoney(i.quantity * i.rate, inv.currency)} |`).join('\n')}

|   | **Subtotal** |   |   | **${formatMoney(sub, inv.currency)}** |
|   | Tax (${inv.taxRate}%) |   |   | ${formatMoney(tax, inv.currency)} |
|   | **TOTAL** |   |   | **${formatMoney(tot, inv.currency)}** |

${inv.paymentLink ? `\n**Pay online:** ${inv.paymentLink}\n` : ''}

---

${inv.notes}
`;
}

export function exportInvoiceHTML(inv: Invoice, logoDataUrl?: string): string {
  const sub = subtotal(inv.items);
  const tax = taxAmount(inv.items, inv.taxRate);
  const tot = total(inv.items, inv.taxRate);
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${inv.number}</title>
<style>
body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#0a1628;max-width:800px;margin:40px auto;padding:32px;background:#fff}
h1{font-size:28px;margin:0 0 8px;color:#0a1628}
.muted{color:#64748b;font-size:13px}
.row{display:flex;justify-content:space-between;gap:32px;margin:24px 0}
.row > div{flex:1}
table{width:100%;border-collapse:collapse;margin:24px 0;font-size:14px}
th,td{padding:12px;text-align:left;border-bottom:1px solid #e5e7eb}
th{background:#f9fafb;font-weight:600;color:#0a1628}
.totals{margin-top:24px;margin-left:auto;width:300px}
.totals .line{display:flex;justify-content:space-between;padding:8px 0}
.totals .total{font-weight:700;font-size:18px;border-top:2px solid #0a1628;padding-top:12px;color:#d4af37}
.pay{margin:24px 0;padding:16px;background:#fef3c7;border-radius:8px;font-size:14px}
.pay a{color:#b45309;font-weight:600}
.notes{margin-top:32px;padding-top:24px;border-top:1px solid #e5e7eb;color:#475569;font-size:13px}
.status{display:inline-block;padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em}
.status.draft{background:#e5e7eb;color:#374151}
.status.sent{background:#dbeafe;color:#1e40af}
.status.paid{background:#dcfce7;color:#166534}
.status.overdue{background:#fee2e2;color:#991b1b}
</style></head>
<body>
${logoDataUrl ? `<img src="${logoDataUrl}" style="max-height:60px;margin-bottom:16px"/>` : ''}
<h1>Invoice ${inv.number}</h1>
<span class="status ${inv.status}">${inv.status}</span>
<div class="row">
  <div><div class="muted">From</div><strong>${inv.from.name}</strong><br/>${inv.from.address.replace(/\n/g, '<br/>')}<br/>${inv.from.email}${inv.from.taxId ? `<br/>Tax ID: ${inv.from.taxId}` : ''}</div>
  <div><div class="muted">Bill to</div><strong>${inv.to.name}</strong><br/>${inv.to.address.replace(/\n/g, '<br/>')}<br/>${inv.to.email}</div>
  <div><div class="muted">Invoice date</div>${inv.date}<div class="muted" style="margin-top:8px">Due</div>${inv.dueDate}</div>
</div>
<table>
  <thead><tr><th>#</th><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>
    ${inv.items.map((i, ix) => `<tr><td>${ix + 1}</td><td>${i.description}</td><td style="text-align:right">${i.quantity}</td><td style="text-align:right">${formatMoney(i.rate, inv.currency)}</td><td style="text-align:right">${formatMoney(i.quantity * i.rate, inv.currency)}</td></tr>`).join('')}
  </tbody>
</table>
<div class="totals">
  <div class="line"><span>Subtotal</span><span>${formatMoney(sub, inv.currency)}</span></div>
  <div class="line"><span>Tax (${inv.taxRate}%)</span><span>${formatMoney(tax, inv.currency)}</span></div>
  <div class="line total"><span>TOTAL</span><span>${formatMoney(tot, inv.currency)}</span></div>
</div>
${inv.paymentLink ? `<div class="pay">💳 <a href="${inv.paymentLink}">Pay this invoice online →</a></div>` : ''}
<div class="notes">${inv.notes.replace(/\n/g, '<br/>')}</div>
</body></html>`;
}

export function exportCSV(invoices: Invoice[]): string {
  const headers = ['Number', 'Date', 'Due', 'Status', 'Client', 'Currency', 'Subtotal', 'Tax', 'Total'];
  const rows = invoices.map(inv => {
    const sub = subtotal(inv.items);
    const tax = taxAmount(inv.items, inv.taxRate);
    const tot = total(inv.items, inv.taxRate);
    return [inv.number, inv.date, inv.dueDate, inv.status, inv.to.name, inv.currency, sub.toFixed(2), tax.toFixed(2), tot.toFixed(2)];
  });
  return [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
}

export function ytdSummary(invoices: Invoice[]): { totalPaid: number; totalOutstanding: number; totalOverdue: number; countByStatus: Record<string, number> } {
  const year = new Date().getFullYear();
  const thisYear = invoices.filter(i => i.date.startsWith(String(year)));
  const totalPaid = thisYear.filter(i => i.status === 'paid').reduce((s, i) => s + total(i.items, i.taxRate), 0);
  const totalOutstanding = thisYear.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + total(i.items, i.taxRate), 0);
  const totalOverdue = thisYear.filter(i => isOverdue(i)).reduce((s, i) => s + total(i.items, i.taxRate), 0);
  const countByStatus = thisYear.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {} as Record<string, number>);
  return { totalPaid, totalOutstanding, totalOverdue, countByStatus };
}
